import { portsByChar } from "../../containers/User/Calculator/actions";

const num = (v) => {
  const n = typeof v === "string" && v.trim() !== "" ? Number(v.replace(/,/g, "")) : v;
  return typeof n === "number" && Number.isFinite(n) ? n : undefined;
};
const text = (v) => (typeof v === "string" && v.trim() ? v.trim() : undefined);

const NUMBER_FIELDS = ["vesselIMO", "vesselMMSI", "vesselBuilt", "vesselDwt", "vesselDraftS", "vesselTPC", "vesselGrainCap", "vesselBaleCap", "vesselLOA", "vesselBeam", "vesselGRT", "vesselNRT", "vesselLightShip"];
const TEXT_FIELDS = ["vesselName", "vesselFlag", "vesselClass", "vesselGears", "vesselGrabs"];

// AI "main / eca / mgo" columns are the form's HS / LS / MGO fuel fields.
const FUELS = { main: "HS", eca: "LS", mgo: "MGO" };

// Turns the AI vessel JSON into a patch for the Create New Vessel form state.
// Sea rows also tick their "Comb Selected" checkbox, which is what enables the row in the form.
export const vesselPatchFromAI = (ai = {}) => {
  const patch = {};
  const set = (k, v) => { if (v !== undefined) patch[k] = v; };

  NUMBER_FIELDS.forEach((k) => set(k, num(ai[k])));
  TEXT_FIELDS.forEach((k) => set(k, text(ai[k])));
  if (["Yes", "No"].includes(ai.vesselScrubber)) patch.vesselScrubber = ai.vesselScrubber;

  [["ballast", "Bal", "Bal"], ["laden", "Ldn", "Lad"]].forEach(([key, prefix, selPrefix]) => {
    (Array.isArray(ai[key]) ? ai[key] : []).slice(0, 4).forEach((row, i) => {
      const n = i + 1;
      const values = { [`vessel${prefix}Spd${n}`]: num(row?.speed) };
      Object.entries(FUELS).forEach(([f, suffix]) => { values[`vessel${prefix}Consm${suffix}${n}`] = num(row?.[f]); });
      if (Object.values(values).every((v) => v === undefined)) return;
      Object.entries(values).forEach(([k, v]) => set(k, v));
      patch[`vessel${selPrefix}Comb${n}Selected`] = true;
    });
  });

  [["idle", "PIdle"], ["loading", "PLdg"], ["discharging", "PDisch"], ["extra", "VExtra"]].forEach(([key, name]) => {
    Object.entries(FUELS).forEach(([f, suffix]) => set(`vessel${name}Consm${suffix}`, num(ai.port?.[key]?.[f])));
  });

  set("hirePerDay", num(ai.hirePerDay));
  set("hireGbb", num(ai.hireGbb));
  set("extraExpense", num(ai.extraExpense));
  set("constants", num(ai.constants));

  const openDate = ai.openDate && new Date(ai.openDate);
  if (openDate && !Number.isNaN(openDate.getTime())) patch.dateOpen = openDate.toISOString();

  const splitHire = (Array.isArray(ai.splitHire) ? ai.splitHire : [])
    .map((r) => ({ NoDays: num(r?.days), applied: 0, disable: false, hireRate: num(r?.rate) }))
    .filter((r) => r.NoDays !== undefined || r.hireRate !== undefined)
    .map((r) => ({ ...r, NoDays: r.NoDays || 0, hireRate: r.hireRate || 0 }));
  if (splitHire.length) patch.splitHire = JSON.stringify(splitHire);

  // [AI key, form field prefix, fuel -> suffix]. Note the form's own spelling "Settlemenths".
  const LOWER = { main: "hs", eca: "ls", mgo: "mgo" };
  [
    [ai.bunker?.onDelivery, "bnkDel"], [ai.bunker?.expRedelivery, "bnkQtyRedelivery"], [ai.bunker?.tolerance, "bnkRedeliveryQtyTolerance"],
    [ai.bunkerPrices?.onDelivery, "bnkPriceDelivery"], [ai.bunkerPrices?.onRedelivery, "bnkPriceRedelivery"], [ai.bunkerPrices?.forSettlement, "bnkPriceSettlement"],
  ].forEach(([row, prefix]) => {
    Object.entries(LOWER).forEach(([f, suffix]) => {
      const n = num(row?.[f]);
      if (n === undefined) return;
      patch[prefix === "bnkPriceSettlement" && suffix === "hs" ? "bnkPriceSettlemenths" : `${prefix}${suffix}`] = n;
      if (prefix.startsWith("bnkPrice")) patch.bunkerPriceOverridden = true; // price cells only take input when overridden
    });
  });

  return patch;
};

// Matches the port search used by the form; returns a selectedPort object or null (user picks manually).
export const resolveVesselPort = async (name) => {
  if (!text(name)) return null;
  try {
    const list = (await portsByChar(name.trim()))?.data?.list || [];
    const exact = list.filter((p) => p.name.toLowerCase() === name.trim().toLowerCase());
    const hit = exact.length === 1 ? exact[0] : list.length === 1 ? list[0] : null;
    return hit ? { ...hit, portLabel: `${hit.name}${hit.country ? ` (${hit.country})` : ""}` } : null;
  } catch (e) {
    return null;
  }
};
